import React, { useCallback, useState, useRef, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import PauseIcon from '@material-ui/icons/Pause'
import { CapeeshColors } from '../../../../../assets/ColorPalette'

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '8px 0',
    },
    playerRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        maxWidth: 400,
        padding: '18px 22px',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 16,
        backdropFilter: 'blur(8px)',
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            padding: '14px 16px',
            gap: 10,
        },
    },
    playButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: 'none',
        backgroundColor: CapeeshColors.BlueBright,
        cursor: 'pointer',
        flexShrink: 0,
        '@media (max-width: 599px)': {
            width: 36,
            height: 36,
        },
    },
    playIcon: {
        fontSize: 24,
        color: '#FFFFFF',
    },
    sliderContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    sliderTrack: {
        width: '100%',
        height: 4,
        borderRadius: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
    },
    sliderFill: {
        height: '100%',
        backgroundColor: CapeeshColors.BlueBright,
        transition: 'width 100ms linear',
    },
    timeLabel: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.5)',
    },
    label: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: 'rgba(255, 255, 255, 0.6)',
    },
}))

function formatTime(seconds) {
    if (!isFinite(seconds) || isNaN(seconds)) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * ListenButtonWithSlider — audio player with progress slider (interactiveElementType === 8).
 * Non-scored; auto-completes after first play.
 */
const ListenButtonWithSlider = ({ testOption, audioMap, onComplete }) => {
    const classes = useStyles()
    const [isPlaying, setIsPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [hasPlayed, setHasPlayed] = useState(false)
    const audioRef = useRef(null)
    const intervalRef = useRef(null)

    const blobUrl = testOption.textAudioUrl
        ? audioMap[testOption.textAudioUrl]
        : null

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.src = ''
            }
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    const handlePlayPause = useCallback(() => {
        if (!blobUrl) return

        if (isPlaying && audioRef.current) {
            audioRef.current.pause()
            setIsPlaying(false)
            if (intervalRef.current) clearInterval(intervalRef.current)
            return
        }

        if (!audioRef.current) {
            audioRef.current = new Audio(blobUrl)
            audioRef.current.onloadedmetadata = () => {
                setDuration(audioRef.current.duration)
            }
            audioRef.current.onended = () => {
                setIsPlaying(false)
                setProgress(100)
                if (intervalRef.current) clearInterval(intervalRef.current)
                if (!hasPlayed) {
                    setHasPlayed(true)
                    if (onComplete) {
                        onComplete({
                            testOptionId: testOption.testOptionId,
                            answers: [],
                        })
                    }
                }
            }
        }

        audioRef.current.play().then(() => {
            setIsPlaying(true)
            intervalRef.current = setInterval(() => {
                if (audioRef.current) {
                    const ct = audioRef.current.currentTime
                    const dur = audioRef.current.duration || 1
                    setCurrentTime(ct)
                    setProgress((ct / dur) * 100)
                }
            }, 100)
        }).catch(() => {
            setIsPlaying(false)
        })
    }, [blobUrl, isPlaying, hasPlayed, testOption.testOptionId, onComplete])

    return (
        <div className={classes.container}>
            <div className={classes.playerRow}>
                <button className={classes.playButton} onClick={handlePlayPause}>
                    {isPlaying ? (
                        <PauseIcon className={classes.playIcon} />
                    ) : (
                        <PlayArrowIcon className={classes.playIcon} />
                    )}
                </button>
                <div className={classes.sliderContainer}>
                    <div className={classes.sliderTrack}>
                        <div
                            className={classes.sliderFill}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <Typography className={classes.timeLabel}>
                        {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>
                </div>
            </div>
            {testOption.text && (
                <Typography className={classes.label}>
                    {testOption.text}
                </Typography>
            )}
        </div>
    )
}

export default ListenButtonWithSlider
